import React, { useState } from "react";
import { ALL_LANES } from "src/types/global";
import { twMerge } from "tailwind-merge";
import { usePatchStore } from "src/services/patch-ecosystem-store";
import {
  ChampionIcon,
  ChampionTier,
  ChampionTierScale,
  GetScalingColorsForChamp,
  getCounterersForChamp,
} from "@procomps/shared";

import { BsCheckLg, BsFillCaretDownFill, BsFillCaretUpFill } from "react-icons/bs";
import { BiRefresh } from "react-icons/bi";

function TierListTable() {
  const { championDB, hardCounterChampionDB } = usePatchStore();
  const [selectedLane, setSelectedLane] = useState<string>(ALL_LANES[0]);

  const getTierRank = (tier: typeof ChampionTierScale): number => {
    const tierRanks: Record<typeof ChampionTierScale, number> = {
      [ChampionTierScale.S]: 1,
      [ChampionTierScale.A]: 2,
      [ChampionTierScale.B]: 3,
      [ChampionTierScale.C]: 4,
      [ChampionTierScale.D]: 5,
    };
    return tierRanks[tier] || 999;
  };

  const getSortedChampionsByTier = (champions: any[], lane: string) => {
    return champions
      .filter((ch) => ch["Tier_List_" + lane].length > 0)
      .sort((a, b) => {
        return getTierRank(a["Tier_List_" + lane]) - getTierRank(b["Tier_List_" + lane]);
      });
  };

  const getFlexLanesForChamp = (champ: any) => {
    const flexLanes = ALL_LANES.filter((lane) => {
      const flexLaneLength = champ["Tier_List_" + lane].length;
      const isEqualToSelectedLane = lane === selectedLane;
      return flexLaneLength > 0 && !isEqualToSelectedLane;
    });
    return flexLanes;
  };

  const getPatchInfoIcon = (champion: any) => {
    if (champion["Buff_Up_Arrow_" + selectedLane] === "Yes") {
      return <BsFillCaretUpFill color="#22c55e" />;
    } else if (champion["Nerf_Down_Arrow_" + selectedLane] === "Yes") {
      return <BsFillCaretDownFill color="#c52222" />;
    } else if (champion["Updated_Icon"] === "Yes") {
      return <BiRefresh color="#8E8E8E" />;
    } else {
      return null;
    }
  };

  return (
    <div className="flex w-full max-w-[1855px]  flex-col items-start justify-center gap-10">
      <div className="flex w-full items-center justify-between">
        <div className="font-unbounded text-[20px] font-extrabold uppercase leading-6 text-neutral-0 ">
          Full Champion Breakdown
        </div>
        <div className="max-w-[572px] font-poppins text-[#8E8E8E] ">
          Dive into the complete data table of all champions this patch, including win rates, pick
          rates, ban rates, and more. Perfect for analysts and players seeking in-depth balance
          insights.
        </div>
      </div>
      <div className="flex w-full min-w-fit flex-col items-center justify-start gap-12">
        <div className="flex w-full items-center justify-center gap-4">
          {ALL_LANES.map((lane) => (
            <div
              className={twMerge(
                "flex h-14 w-14 cursor-pointer items-center justify-center rounded-full border-[1.4px] border-neutral-900 bg-neutral-900 ",
                lane === selectedLane && "border-primary bg-[#00B29F1A] ",
              )}
              key={lane}
              onClick={() => setSelectedLane(lane)}
            >
              <img
                className="h-1/2 w-1/2"
                src={require(`src/assets/icons/lanes_primary/${lane}.PNG`)}
                alt={lane}
              />
            </div>
          ))}
        </div>
        <div className="flex w-full flex-col items-center justify-start gap-8">
          <div className="flex h-6 w-full items-center justify-start gap-[65.14px] font-poppins text-base font-medium text-[#717171]">
            <div className="flex h-full min-w-[163px] items-center justify-between ">
              <span>Rank</span>
              <span>Tier</span>
            </div>
            <div className="flex h-full min-w-[211px] items-center justify-start ">Champion</div>
            <div className="flex h-full min-w-[272px] items-center justify-between pr-[25px] ">
              <span>Primary</span>
              <span>Secondary</span>
            </div>
            <div className="flex h-full min-w-[180px] items-center justify-start ">Counters</div>
            <div className="flex h-full min-w-[116px] items-center justify-start ">Scaling</div>
            <div className="flex h-full min-w-[154px] items-center justify-start ">Flex</div>
            <div className="flex h-full min-w-[71px] items-center justify-start ">Blind Pick</div>
            <div className="flex h-full min-w-[80px] items-center justify-start ">Patch</div>
          </div>
        </div>
        <div className="flex w-full flex-col items-center justify-start gap-4">
          {getSortedChampionsByTier(championDB, selectedLane).map((champion, idx) => (
            <div className="flex w-full flex-col items-center justify-center gap-4">
              <div className="flex h-14 w-full items-center justify-start gap-[65.14px] " key={idx}>
                <div className="flex h-full w-[163px] items-center justify-between">
                  <span className="text-center font-unbounded text-[20px] font-extrabold leading-6 text-neutral-0 ">
                    {idx + 1}
                  </span>
                  <ChampionTier
                    className="text-center text-[40px] font-extrabold max-[650px]:text-[30px] "
                    tier={champion["Tier_List_" + selectedLane]}
                    size="large"
                  />
                </div>
                <div className="flex h-full w-[211px] items-center justify-start gap-2 ">
                  <ChampionIcon champion={champion.Champion} className="h-14 w-14" />
                  <span className="font-poppins text-base  font-extrabold  text-[#e3e3e3] ">
                    {champion.Champion}
                  </span>
                </div>
                <div className="flex h-full w-[272px] items-center justify-between ">
                  <span className="font-unbounded text-base  font-extrabold  text-[#e3e3e3] ">
                    {champion["1st_gamestyle"]}
                  </span>
                  <span className="flex min-w-[94.4px] items-center justify-start pr-[25px] font-unbounded text-base  font-extrabold  text-[#e3e3e3] ">
                    {champion["2nd_gamestyle"]}
                  </span>
                </div>
                <div className="flex h-full w-[180px] items-center justify-start ">
                  {getCounterersForChamp(hardCounterChampionDB, champion.Champion)
                    .slice(0, 4)
                    .map((counter: string) => (
                      <ChampionIcon
                        key={counter}
                        champion={counter}
                        className="-ml-4 h-[54px] w-[54px] border-[6px] border-[#000605] "
                      />
                    ))}
                </div>
                <div className="flex h-full w-[116px] items-center justify-start ">
                  <div className=" flex items-center justify-start gap-2">
                    <GetScalingColorsForChamp champion={champion} />
                  </div>
                </div>
                <div className="flex h-full w-[154px] items-center justify-start gap-2 ">
                  {getFlexLanesForChamp(champion).map((lane) => (
                    <img
                      key={lane}
                      src={require(`src/assets/icons/lanes_primary/${lane}.PNG`)}
                      alt={lane}
                      className="h-8 w-8"
                    />
                  ))}
                </div>
                <div className="flex h-full w-[71px] items-center justify-start ">
                  {champion["Blind_Pick"] === "Yes" ? (
                    <BsCheckLg color="#15803d" size={32} />
                  ) : (
                    <></>
                  )}
                </div>
                <div className="flex h-full w-[80px] items-center justify-start text-[28px] ">
                  {getPatchInfoIcon(champion)}
                </div>
              </div>
              <div className="h-[1px] w-full bg-neutral-900" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default TierListTable;
